/**
 * Google Analytics 4 Helper Functions
 * Provides easy-to-use methods for tracking events throughout the application
 * 
 * Setup Instructions:
 * 1. Create a Google Analytics 4 property at https://analytics.google.com
 * 2. Get your Measurement ID (format: G-XXXXXXXXXX)
 * 3. Replace G-XXXXXXXXXX in public/index.html with your actual ID
 * 4. Deploy and verify tracking in GA4 Real-time reports
 */

/**
 * Track a page view
 * Automatically called by React Router, but can be called manually if needed
 * 
 * @param {string} path - The page path (e.g., '/about', '/services')
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
    console.log(`📊 GA4: Page view tracked - ${path}`);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

/**
 * Track contact form submission
 * Call this when a user successfully submits a contact form
 * 
 * @param {string} formType - Type of form ('popup', 'standalone', 'contact_page')
 * @param {object} formData - Form data (name, email, etc.)
 */
export const trackFormSubmission = (formType, formData = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'form_submission', {
      event_category: 'Contact',
      event_label: formType,
      form_type: formType,
      user_email: formData.email || 'not_provided',
      challenge_type: formData.challenge || 'not_specified',
      value: 1,
    });
    console.log(`📊 GA4: Form submission tracked - ${formType}`);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

/**
 * Track button clicks
 * Use for important CTAs and navigation
 * 
 * @param {string} buttonName - Name/label of the button clicked
 * @param {string} location - Where the button is located (e.g., 'header', 'hero', 'footer')
 */
export const trackButtonClick = (buttonName, location = 'unknown') => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'button_click', {
      event_category: 'Engagement',
      event_label: buttonName,
      button_name: buttonName,
      button_location: location,
    });
    console.log(`📊 GA4: Button click tracked - ${buttonName} (${location})`);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

/**
 * Track link clicks (external links, email, phone)
 * 
 * @param {string} linkType - Type of link ('external', 'email', 'phone', 'download')
 * @param {string} linkUrl - The URL or destination
 */
export const trackLinkClick = (linkType, linkUrl) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'link_click', {
      event_category: 'Navigation',
      event_label: linkType,
      link_type: linkType,
      link_url: linkUrl,
    });
    console.log(`📊 GA4: Link click tracked - ${linkType}: ${linkUrl}`);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

/**
 * Track service page views
 * Track which services users are most interested in
 * 
 * @param {string} serviceName - Name of the service viewed
 */
export const trackServiceView = (serviceName) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'service_view', {
      event_category: 'Services',
      event_label: serviceName,
      service_name: serviceName,
    });
    console.log(`📊 GA4: Service view tracked - ${serviceName}`);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

/**
 * Track errors and issues
 * Useful for understanding user problems
 * 
 * @param {string} errorType - Type of error ('form_error', 'navigation_error', etc.)
 * @param {string} errorMessage - Error message or description
 */
export const trackError = (errorType, errorMessage) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'error', {
      event_category: 'Error',
      event_label: errorType,
      error_type: errorType,
      error_message: errorMessage,
    });
    console.log(`📊 GA4: Error tracked - ${errorType}: ${errorMessage}`);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

/**
 * Track scroll depth
 * Understand how far users scroll on long pages
 * 
 * @param {number} percentage - Scroll percentage (25, 50, 75, 100)
 * @param {string} pagePath - Current page path
 */
export const trackScrollDepth = (percentage, pagePath) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'scroll_depth', {
      event_category: 'Engagement',
      event_label: `${percentage}%`,
      scroll_percentage: percentage,
      page_path: pagePath,
    });
    console.log(`📊 GA4: Scroll depth tracked - ${percentage}% on ${pagePath}`);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

/**
 * Track time on page
 * Measure engagement time on important pages
 * 
 * @param {string} pagePath - Current page path
 * @param {number} timeInSeconds - Time spent on page in seconds
 */
export const trackTimeOnPage = (pagePath, timeInSeconds) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'time_on_page', {
      event_category: 'Engagement',
      event_label: pagePath,
      page_path: pagePath,
      time_seconds: timeInSeconds,
      value: timeInSeconds,
    });
    console.log(`📊 GA4: Time on page tracked - ${timeInSeconds}s on ${pagePath}`);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

/**
 * Track custom events
 * Generic function for any custom event not covered above
 * 
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Event parameters
 */
export const trackCustomEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, eventParams);
    console.log(`📊 GA4: Custom event tracked - ${eventName}`, eventParams);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

/**
 * Set user properties
 * Track user attributes for better segmentation
 * 
 * @param {object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('set', 'user_properties', properties);
    console.log('📊 GA4: User properties set', properties);
  } else {
    console.warn('Google Analytics not loaded');
  }
};

// Export all functions as default export for convenience
const analytics = {
  trackPageView,
  trackFormSubmission,
  trackButtonClick,
  trackLinkClick,
  trackServiceView,
  trackError,
  trackScrollDepth,
  trackTimeOnPage,
  trackCustomEvent,
  setUserProperties,
};

export default analytics;
